import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * Horizontal scroll container with a custom, ALWAYS-VISIBLE scrollbar.
 *
 * The native scrollbar is hidden (it auto-hides as an overlay bar on touch
 * devices / DevTools responsive mode / Windows fluent scrollbars). Instead we
 * render our own track + thumb that stays on screen permanently and supports
 * dragging the thumb and clicking the track.
 */
export const CustomScrollRow = ({ viewportClassName, trackClassName, children }) => {
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const dragRef = useRef(null);
  // width/left are percentages of the track; scrollable tells us whether the
  // content overflows (thumb only makes sense when there is room to scroll).
  const [thumb, setThumb] = useState({ width: 100, left: 0, scrollable: false });

  const updateThumb = useCallback(() => {
    const el = viewportRef.current;
    if (!el) return;
    const { scrollWidth, clientWidth, scrollLeft } = el;
    const scrollable = scrollWidth > clientWidth + 1;
    const widthPct = scrollable ? (clientWidth / scrollWidth) * 100 : 100;
    const leftPct = scrollable ? (scrollLeft / scrollWidth) * 100 : 0;
    setThumb({ width: widthPct, left: leftPct, scrollable });
  }, []);

  // Keep the thumb in sync with content size changes (data load, resize, etc.).
  useEffect(() => {
    updateThumb();
    const el = viewportRef.current;
    let ro;
    if (el && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(updateThumb);
      ro.observe(el);
      if (el.firstElementChild) ro.observe(el.firstElementChild);
    }
    window.addEventListener("resize", updateThumb);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", updateThumb);
    };
  }, [updateThumb, children]);

  const scrollByThumbDelta = (deltaPx) => {
    const el = viewportRef.current;
    const track = trackRef.current;
    if (!el || !track) return;
    const trackWidth = track.clientWidth || 1;
    // Moving the thumb across the track maps to moving across scrollWidth.
    el.scrollLeft += (deltaPx / trackWidth) * el.scrollWidth;
  };

  const onThumbMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragRef.current = { lastX: e.clientX };
    const onMove = (ev) => {
      if (!dragRef.current) return;
      const x = ev.touches ? ev.touches[0].clientX : ev.clientX;
      scrollByThumbDelta(x - dragRef.current.lastX);
      dragRef.current.lastX = x;
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
  };

  // Click anywhere on the track to jump the viewport to that position.
  const onTrackMouseDown = (e) => {
    const el = viewportRef.current;
    const track = trackRef.current;
    if (!el || !track) return;
    const rect = track.getBoundingClientRect();
    const clickRatio = (e.clientX - rect.left) / (rect.width || 1);
    el.scrollLeft = clickRatio * el.scrollWidth - el.clientWidth / 2;
  };

  return (
    <div className="sl-scroll-shell">
      <div ref={viewportRef} className={viewportClassName} onScroll={updateThumb}>
        {children}
      </div>
      <div
        ref={trackRef}
        className={`sl-cbar-track ${trackClassName || ""}`}
        onMouseDown={onTrackMouseDown}
      >
        <div
          className="sl-cbar-thumb"
          style={{
            width: `${thumb.width}%`,
            left: `${thumb.left}%`,
            opacity: thumb.scrollable ? 1 : 0.45,
          }}
          onMouseDown={onThumbMouseDown}
          onTouchStart={onThumbMouseDown}
        />
      </div>
    </div>
  );
};

export default CustomScrollRow;
